import React from 'react';

import { defineCatalogElement } from '@archef2000/react-planner';
import * as Three from 'three';

const grey = new Three.MeshLambertMaterial({ color: 0x3f3f3f });
const white = new Three.MeshLambertMaterial({
  color: 0xffffff,
  transparent: true,
  opacity: 0.5
});
const black = new Three.MeshLambertMaterial({ color: 0x000000 });

function makeDoor(handleSide: boolean) {
  const slidingDoor = new Three.Mesh();

  const doorShape = new Three.Shape();
  doorShape.moveTo(1, 2);
  doorShape.lineTo(0, 2);
  doorShape.lineTo(0, 0);
  doorShape.lineTo(1, 0);

  const doorHole = new Three.Path();
  doorHole.moveTo(0.65, 1.75);
  doorHole.lineTo(0.35, 1.75);
  doorHole.lineTo(0.35, 1.25);
  doorHole.lineTo(0.65, 1.25);
  doorShape.holes.push(doorHole);

  const extrudeSettings = {
    steps: 2,
    depth: 0.05,
    bevelEnabled: false,
    bevelThickness: 1,
    bevelSize: 1,
    bevelSegments: 1
  };

  const geometry = new Three.ExtrudeGeometry(doorShape, extrudeSettings);
  const door = new Three.Mesh(geometry, grey);
  if (handleSide) {
    door.position.set(-1, 0, 0);
  } else {
    door.position.set(0, 0, 0);
  }
  slidingDoor.add(door);

  const doorGeometry = new Three.BoxGeometry(1, 2, 0.05);
  const door2 = new Three.Mesh(doorGeometry, grey);
  if (handleSide) {
    door2.position.set(1.5, 1, 0.065);
  } else {
    door2.position.set(-0.5, 1, 0.065);
  }
  door.add(door2);

  const barGeometry = new Three.BoxGeometry(2, 0.1, 0.1);
  const doorBar = new Three.Mesh(barGeometry, grey);
  doorBar.position.set(-0, 2.07, 0.05);
  slidingDoor.add(doorBar);

  const glassGeometry = new Three.BoxGeometry(0.3, 0.5, 0.05);
  const glass = new Three.Mesh(glassGeometry, white);
  glass.position.set(0.5, 1.5, 0.025);
  door.add(glass);

  const HandleGeometry1 = new Three.CylinderGeometry(
    0.051,
    0.051,
    0.0625,
    80,
    80,
    true
  );
  black.side = Three.DoubleSide;
  const handle_p1 = new Three.Mesh(HandleGeometry1, black);
  handle_p1.position.set(0.2, 1, 0.025);
  handle_p1.rotation.x = Math.PI / 2;
  door.add(handle_p1);

  const HandleGeometry2 = new Three.BoxGeometry(0.1, 0.02, 0.0625);
  const handle_p2 = new Three.Mesh(HandleGeometry2, black);
  handle_p2.position.set(0.2, 1, 0.025);
  door.add(handle_p2);

  if (!handleSide) {
    handle_p1.position.x = 0.8;
    handle_p2.position.x = 0.8;
  }

  return slidingDoor;
}

export default defineCatalogElement({
  name: 'sliding door',
  prototype: 'holes',

  info: {
    tag: ['door'],
    title: 'sliding door',
    description: 'iron door',
    image: require('./slidingDoor.png')
  },

  properties: {
    width: {
      label: 'width',
      type: 'length-measure',
      defaultValue: {
        length: 200
      }
    },
    height: {
      label: 'height',
      type: 'length-measure',
      defaultValue: {
        length: 215
      }
    },
    thickness: {
      label: 'thickness',
      type: 'length-measure',
      defaultValue: {
        length: 30
      }
    },
    altitude: {
      label: 'altitude',
      type: 'length-measure',
      defaultValue: {
        length: 0
      }
    },
    flip_horizontal: {
      label: 'horizontal flip',
      type: 'checkbox',
      defaultValue: false,
      values: {
        none: false,
        yes: true
      }
    },
    flip_vertical: {
      label: 'vertical flip',
      type: 'checkbox',
      defaultValue: true,
      values: {
        left: false,
        right: true
      }
    }
  },

  render2D: function (element, layer, scene) {
    const STYLE_HOLE_BASE = {
      stroke: '#000',
      strokeWidth: '14px',
      fill: '#000'
    };
    const STYLE_HOLE_BASE2 = {
      stroke: '#000',
      strokeWidth: '16px',
      fill: '#000'
    };
    const STYLE_HOLE_SELECTED = {
      stroke: '#0096fd',
      strokeWidth: '14px',
      fill: '#0096fd',
      cursor: 'move'
    };

    const epsilon = 3;
    const flip: boolean = element.properties.flip_horizontal;
    const handleSide: boolean = element.properties.flip_vertical;
    const holeWidth = element.properties.width.length;
    const holeStyle = element.selected ? STYLE_HOLE_SELECTED : STYLE_HOLE_BASE;
    const holeStyle2 = element.selected
      ? STYLE_HOLE_SELECTED
      : STYLE_HOLE_BASE2;

    let scaleX: number, scaleY: number;
    let pX1: number, pX2: number;

    if (flip) {
      scaleX = 1;
      if (handleSide) {
        pX1 = 0;
        pX2 = holeWidth / 2;
        scaleY = -1;
      } else {
        pX1 = holeWidth / 2;
        pX2 = holeWidth;
        scaleY = -1;
      }
    } else {
      scaleX = 1;
      if (handleSide) {
        pX1 = holeWidth / 2;
        pX2 = holeWidth;
        scaleY = 1;
      } else {
        pX1 = 0;
        pX2 = holeWidth / 2;
        scaleY = 1;
      }
    }

    return (
      <g transform={`translate(${-element.properties.width.length / 2}, 0)`}>
        <line
          key="1"
          x1="0"
          y1={0 - epsilon}
          x2={holeWidth}
          y2={0 - epsilon}
          style={holeStyle}
          transform={`scale(${scaleX},${scaleY})`}
        />
        <line
          key="2"
          x1={pX1}
          y1={5 - epsilon}
          x2={pX2}
          y2={5 - epsilon}
          style={holeStyle2}
          transform={`scale(${scaleX},${scaleY})`}
        />
        <line
          key="3"
          x1={holeWidth}
          y1={0 - epsilon}
          x2={holeWidth}
          y2={15 + epsilon}
          style={holeStyle2}
          transform={`scale(${scaleX},${scaleY})`}
        />
        <line
          key="4"
          x1="0"
          y1={0 - epsilon}
          x2="0"
          y2={15 + epsilon}
          style={holeStyle2}
          transform={`scale(${scaleX},${scaleY})`}
        />
      </g>
    );
  },

  async render3D(element, layer, scene) {
    const flip: boolean = element.properties.flip_horizontal;
    const handleSide: boolean = element.properties.flip_vertical;
    const width = element.properties.width.length;
    const height = element.properties.height.length;
    const thickness = element.properties.thickness.length;
    const newAltitude = element.properties.altitude.length;

    const slidingDoor = new Three.Object3D();
    slidingDoor.add(makeDoor(handleSide).clone());

    const valuePosition = new Three.Box3().setFromObject(slidingDoor);

    const deltaX = Math.abs(valuePosition.max.x - valuePosition.min.x);
    const deltaY = Math.abs(valuePosition.max.y - valuePosition.min.y);
    const deltaZ = Math.abs(valuePosition.max.z - valuePosition.min.z);

    if (element.selected) {
      const boundingBox = new Three.BoxHelper(slidingDoor, 0x99c3fb);
      boundingBox.material.linewidth = 5;
      boundingBox.renderOrder = 1000;
      boundingBox.material.depthTest = false;
      slidingDoor.add(boundingBox);
    }

    if (flip) {
      slidingDoor.rotation.y += Math.PI;
    }

    slidingDoor.position.y += newAltitude;
    slidingDoor.scale.set(width / deltaX, height / deltaY, thickness / deltaZ);

    return slidingDoor;
  }
});
